import React, { memo } from "react";
import { NavLink, useLocation } from "react-router-dom";
import FontAwesome from "react-fontawesome";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";

// Shared navigation leaf item used for links, actions, and submenu triggers.
// Wrap component with memo to prevent unnecessary re-renders
const MenuItemWithTooltip = memo(
  ({
    to,
    icon,
    text,
    onClick,
    size,
    component,
    id,
    config,
    name,
    isButton = false,
    isSubmenuItem = false,
    hasSubmenu = false,
    isExpanded = false,
    className = "",
    parentIds = [],
    onCollapseOthers,
    activeMenuItems,
    showTooltip = true,
    isInExpandedSubmenu = true,
  }) => {
    const location = useLocation();

    const isActive =
      !isButton &&
      to &&
      (location.pathname === to ||
        location.pathname === `${to}/` ||
        activeMenuItems?.has(to));

    const handleClick = (e) => {
      if (!hasSubmenu && !isButton && !isSubmenuItem) {
        if (onCollapseOthers && !isActive) {
          onCollapseOthers(parentIds);
        }
      }
      if (onClick) {
        if (component) {
          onClick({ component: component, name: name, id: id, ...config });
        } else onClick(e);
      }
    };

    // If this is a button in a submenu but the submenu is not expanded, don't render
    if (isButton && isSubmenuItem && !isInExpandedSubmenu) {
      return null;
    }

    const hasIcon = Boolean(icon);

    const content = (
      <div
        className={`nav-item-content ${
          size === "small" ? "nav-item-content-small" : ""
        } ${!hasIcon ? "nav-item-content-no-icon" : ""}`}
      >
        {hasIcon && <FontAwesome name={icon} />}
        <span className="nav-item-text">{text}</span>
        {hasSubmenu && (
          <FontAwesome
            name="chevron-right"
            className={`submenu-arrow ${isExpanded ? "expanded" : ""}`}
          />
        )}
      </div>
    );

    // Add button-item class for buttons and never add active class
    const fullClassName = `nav-item ${isButton ? "button-item" : ""} ${
      isSubmenuItem ? "submenu-item" : ""
    } ${hasSubmenu ? "submenu-trigger" : ""} ${
      size === "small" ? "nav-item--small" : ""
    } ${className} ${!isButton && isActive ? "active sidebar-active-pill" : ""}`;

    const tooltipContent = React.isValidElement(text)
      ? text.props?.children?.[0] || ""
      : String(text || "");

    const renderComponent = isButton ? (
      <button className={fullClassName} onClick={handleClick}>
        {content}
      </button>
    ) : (
      <NavLink to={to} className={fullClassName} onClick={handleClick} end>
        {content}
      </NavLink>
    );

    if (!isExpanded && showTooltip) {
      return (
        <OverlayTrigger
          placement="right"
          popperConfig={{
            modifiers: [
              {
                name: "preventOverflow",
                options: { boundary: "viewport", padding: 8 },
              },
              { name: "offset", options: { offset: [0, 8] } },
            ],
          }}
          overlay={
            <Tooltip
              style={{
                position: "fixed",
                pointerEvents: "none",
              }}
            >
              {tooltipContent}
            </Tooltip>
          }
          container={document.body}
        >
          {renderComponent}
        </OverlayTrigger>
      );
    }

    return renderComponent;
  }
);

export default MenuItemWithTooltip;
